import { type ReactNode, useState } from 'react';

import { PrimaryButton, SecondaryButton } from '../../components/buttons';
import { useClipboardCopy } from '../../hooks/use-clipboard-copy';
import { storeLayout } from '../../route-apis';

const MCP_URL = import.meta.env.VITE_MCP_URL;
const CLIENT_ID = import.meta.env.VITE_MCP_CLIENT_ID;
const AUTH0_DOMAIN = import.meta.env.VITE_AUTH0_DOMAIN;
const AUTH_AUTHORIZE_URL = `https://${AUTH0_DOMAIN}/authorize`;
const AUTH_TOKEN_URL = `https://${AUTH0_DOMAIN}/oauth/token`;

const CLIENTS = ['Claude', 'ChatGPT', 'Microsoft Copilot', 'Gemini'] as const;
type Client = (typeof CLIENTS)[number];

// A step is either plain instruction text, or text followed by a credential
// the owner copies and pastes right at that point in their assistant's setup.
// Keeping the value next to the step it's used in (rather than in a separate
// box up top) is the whole point: nothing to scroll back to.
type Credential = { label: string; value: string };
type Step = { text: string; copy?: Credential };

// Steps are taken from each provider's own current connect-a-custom-MCP-server
// documentation (Anthropic Help Center; OpenAI ChatGPT developer mode / apps;
// Microsoft Copilot Studio MCP onboarding wizard).
// Worded for store owners, not developers — keep it that way if you update them.
const STEPS: Record<'Claude' | 'ChatGPT' | 'Microsoft Copilot', Step[]> = {
  Claude: [
    { text: 'In Claude, open Customize, then Connectors.' },
    { text: 'Click the + button, then “Add custom connector”.' },
    {
      text: 'Paste this as the Remote MCP server URL:',
      copy: { label: 'Remote MCP server URL', value: MCP_URL },
    },
    {
      text: 'Click “Advanced settings”, then paste this as the OAuth Client ID, and leave the Client Secret blank:',
      copy: { label: 'OAuth Client ID', value: CLIENT_ID },
    },
    {
      text: 'Click Add. When Claude asks, sign in with your normal Orderbot account and click Allow.',
    },
    {
      text: 'Start a chat, click the + button, turn on the Orderbot connector, then ask: “What’s running low at my store?”',
    },
  ],
  ChatGPT: [
    {
      text: 'In ChatGPT, open Settings → Apps & Connectors → Advanced settings, and turn on Developer mode. (This needs a paid ChatGPT plan.)',
    },
    { text: 'Go back to Apps & Connectors and click “Create”.' },
    {
      text: 'Name it Orderbot and paste this as the Remote MCP server URL:',
      copy: { label: 'Remote MCP server URL', value: MCP_URL },
    },
    {
      text: 'For Authentication choose OAuth, paste this as the OAuth Client ID, and leave the Client Secret blank:',
      copy: { label: 'OAuth Client ID', value: CLIENT_ID },
    },
    { text: 'Check the box to confirm you understand, then click Create.' },
    {
      text: 'Open a new chat, turn on the Orderbot app, sign in with your Orderbot account and click Allow, then ask: “What’s running low at my store?”',
    },
  ],
  'Microsoft Copilot': [
    {
      text: 'In Microsoft Copilot Studio (copilotstudio.microsoft.com), open an agent — or create one — then go to Tools. (You need permission to add tools.)',
    },
    { text: 'Select Add a tool → New tool → Model Context Protocol.' },
    {
      text: 'Name it Orderbot, add a short description, and paste this as the Server URL:',
      copy: { label: 'Server URL', value: MCP_URL },
    },
    { text: 'Under Authentication, choose OAuth 2.0, then Manual.' },
    {
      text: 'Paste this as the Client ID. For Client secret, type unused (Orderbot signs you in securely without a secret):',
      copy: { label: 'OAuth Client ID', value: CLIENT_ID },
    },
    {
      text: 'Paste this as the Authorization URL:',
      copy: { label: 'Authorization URL', value: AUTH_AUTHORIZE_URL },
    },
    {
      text: 'Paste this as both the Token URL template and the Refresh URL:',
      copy: { label: 'Token URL', value: AUTH_TOKEN_URL },
    },
    {
      text: 'Select Create, then Next. Choose Create a new connection, sign in with your Orderbot account, and select Add to agent.',
    },
    {
      text: 'Publish the agent and add the Microsoft 365 Copilot channel. In Microsoft 365 Copilot (m365.cloud.microsoft), open your agent and ask: “What’s running low at my store?” (Requires a Microsoft 365 Copilot license.)',
    },
  ],
};

function CopyField({ label, value }: Credential) {
  const { copied, copy } = useClipboardCopy(value);

  return (
    <div className="mt-3 rounded-lg border border-gray-300 bg-gray-50 p-4">
      <div className="mb-2 text-sm font-semibold uppercase tracking-wide text-gray-600">
        {label}
      </div>
      <div className="flex items-center gap-2">
        <code className="flex-1 break-all rounded border border-gray-200 bg-white px-4 py-3 font-mono text-base text-gray-900">
          {value}
        </code>
        <PrimaryButton
          type="button"
          onClick={() => void copy()}
          aria-pressed={copied}
          aria-label={copied ? `Copied ${label}` : `Copy ${label}`}
        >
          {copied ? 'Copied' : 'Copy'}
        </PrimaryButton>
      </div>
    </div>
  );
}

function TabButton({
  label,
  selected,
  onSelect,
}: {
  label: string;
  selected: boolean;
  onSelect: () => void;
}) {
  const Component = selected ? PrimaryButton : SecondaryButton;

  return (
    <Component type="button" aria-pressed={selected} onClick={onSelect}>
      {label}
    </Component>
  );
}

export function AIAssistant() {
  const { store } = storeLayout.useRouteContext();
  const [client, setClient] = useState<Client>('Claude');

  let body: ReactNode;
  if (client === 'Gemini') {
    body = (
      <p className="text-base text-gray-600">
        Google Gemini can&rsquo;t connect to tools like this yet. As soon as Google supports it,
        step-by-step instructions will appear here.
      </p>
    );
  } else {
    body = (
      <ol className="flex list-decimal flex-col gap-4 pl-6 text-lg text-gray-800">
        {STEPS[client].map((step) => (
          <li key={step.text}>
            <span>{step.text}</span>
            {step.copy && <CopyField label={step.copy.label} value={step.copy.value} />}
          </li>
        ))}
      </ol>
    );
  }

  return (
    <div className="mx-auto flex max-w-2xl flex-col gap-8 px-4 py-10">
      <header>
        <h1 className="text-3xl font-semibold">AI Assistant</h1>
        <p className="mt-1 text-lg text-gray-600">
          Connect Orderbot to {store.name} in your AI assistant. One connection covers all your
          stores.
        </p>
      </header>

      <section className="flex flex-col gap-6">
        <div className="flex gap-2">
          {CLIENTS.map((c) => (
            <TabButton key={c} label={c} selected={c === client} onSelect={() => setClient(c)} />
          ))}
        </div>

        {body}
      </section>
    </div>
  );
}
